// Module-level mirror of the user's active network. Mutated by
// `NetworkSyncProvider` whenever the Zustand store changes; read by
// non-React utility functions that can't subscribe to the store
// (`getUsdcMint`, `getSolscanTxUrl`, `getApiHeaders`, etc.).
//
// Reactivity: component re-renders are NOT triggered by reads of the
// singleton. Components must use `useUserNetwork()` / `useNetworkConfig()`
// to re-render on toggle. Module-level utilities just return the new
// value on their next call after `NetworkSyncProvider` has rebound it,
// which it does during its own render, before any child renders.
//
// SSR / server: the singleton holds the deployment default. Server
// Components that need the user's runtime network should read the
// cookie via `getServerNetwork()` from `./server` instead.

import {
  configureChainAddresses,
  DEFAULT_CHAIN_ADDRESSES,
  parseSellCapProgramFlag,
} from '@morfi/contract-sdk';
import { fallsBackToDevnetUsdc, getUsdcMint } from '@/config/solana';
import { logger } from '@/lib/telemetry/logger';
import { deploymentEnv, getDefaultNetwork, isNetworkConfigured } from './config';
import type { Network } from './types';

let activeNetwork: Network = getDefaultNetwork();
const warnedQuoteMintFallback: Set<Network> = new Set<Network>();

/** Bind the active network for module-level utility functions and the
 *  contract SDK's default program ids / quote mint / program variant (a
 *  browser bundle has no process env for the SDK to read them from). Called
 *  by `NetworkSyncProvider` at load and whenever the network changes. Every
 *  address is passed, so nothing carries over from the previous network. */
export function setActiveNetwork(network: Network): void {
  activeNetwork = network;
  if (!isNetworkConfigured(network)) return;
  const marketsProgramId = resolveMarketsProgramId(network);
  warnOnQuoteMintFallback(network, marketsProgramId);
  configureChainAddresses({
    marketsProgramId,
    cpSwapProgramId:
      deploymentEnv(network, 'CP_SWAP_PROGRAM_ID') ?? DEFAULT_CHAIN_ADDRESSES.cpSwapProgramId,
    quoteMint: getUsdcMint(network),
    sellCapProgram: parseSellCapProgramFlag(deploymentEnv(network, 'SELL_CAP_PROGRAM')),
  });
}

/** The Markets program the SDK and PDA derivations use on `network`.
 *  `NEXT_PUBLIC_PROGRAM_ID_<NETWORK>` only marks the network configured, so a
 *  deployment that still carries a retired program id there keeps trading on
 *  the SDK's own program until `NEXT_PUBLIC_MARKETS_PROGRAM_ID_<NETWORK>` says
 *  otherwise. */
export function resolveMarketsProgramId(network: Network): string {
  return (
    deploymentEnv(network, 'MARKETS_PROGRAM_ID') ??
    DEFAULT_CHAIN_ADDRESSES.marketsProgramId.toBase58()
  );
}

// A deployment with its own Markets program but no quote mint trades the
// devnet USDC: bonding-curve trades still work, while AMM swaps and balances
// silently use the wrong mint. Logged once per network per page load.
function warnOnQuoteMintFallback(network: Network, marketsProgramId: string): void {
  if (warnedQuoteMintFallback.has(network) || !fallsBackToDevnetUsdc(network)) return;
  if (marketsProgramId === DEFAULT_CHAIN_ADDRESSES.marketsProgramId.toBase58()) return;
  warnedQuoteMintFallback.add(network);
  logger.warn('network.quote_mint.default_fallback', {
    network,
    marketsProgramId,
    quoteMint: getUsdcMint(network),
  });
}

/** Read the active network. Use this in non-React utilities; in React
 *  components use `useUserNetwork()` for reactive subscription. */
export function getActiveNetwork(): Network {
  return activeNetwork;
}
